import type {ReactElement} from 'react';
import React, {useContext, useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType, ProductType, PublishingAnalyticsAction} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {ModalDetails} from '@Components/modal/modal-context';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {setErrorMessageForPublishingPanel, setProductType, setWebLinksData} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {useHashedId, usePrintProduct, useShowLoader, useWebLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {processDesignForPublishing} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {
  fetchPublishAsWebpageMeta,
  handlePublishRequest,
  handleUnpublishRequest,
} from '@Panels/poster-publishing-options-panel/components/publishing-as-webpage-panel/publishing-as-webpage-library';
import {useAppDispatch} from '@/hooks';
import styles from './publishing-as-webpage-panel.module.scss';
import type {PublishingAsWebpagePanelProps, PublishingAsWebpageResponse} from './publishing-as-webpage-panel.types';
import {PublishingAsWebpageType} from './publishing-as-webpage-panel.types';
import {CopyLinkButton} from '@Components/copy-link-button/copy-link-button';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';

export function PublishingAsWebpagePanel({onTogglePublish = noop, ...props}: PublishingAsWebpagePanelProps): ReactElement<any> {
  const {displayType, publishItemType = PublishingAsWebpageType.PUBLISH_POSTER} = props;
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const webLink = useWebLinks();
  const [showLoadScreen, setShowLoadScreen] = useState(!webLink.shareableUrl);
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const dispatch = useAppDispatch();
  const showLoader = useShowLoader();
  const userPremiumLevel = getUserPremiumLevel();
  const printProduct: PosterProductDataForProcessDesign | undefined = usePrintProduct();
  const isModalDisplay = displayType === DisplayType.MODAL;

  const hashedIdFromStore = useHashedId();
  const hashedId = props.hashedId ?? hashedIdFromStore;

  useEffect(() => {
    if (!showLoader && ((!webLink.shareableUrl && hashedId) || hashedIdFromStore !== hashedId)) {
      setShowLoadScreen(true);
      fetchInitPublishAsWebpageMeta();
    }
  }, [showLoader]);

  const onFetchPublishAsWebpageMetaSuccess = (response: PublishingAsWebpageResponse): void => {
    dispatch(
      setWebLinksData({
        shareableUrl: response.shareableUrl,
        isPublishedLink: response.isPublishedLink,
        isUrlDisabled: response.isUrlDisabled,
      })
    );
  };

  const fetchInitPublishAsWebpageMeta = (): void => {
    fetchPublishAsWebpageMeta(publishItemType, hashedId)
      .then((response: PublishingAsWebpageResponse) => {
        onFetchPublishAsWebpageMetaSuccess(response);
      })
      .catch(() => {
        dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_publish_webpage_error')));
      })
      .finally(() => {
        setShowLoadScreen(false);
      });
  };

  useEffect(() => {
    const prepareCheckout = document.querySelector('.js-prepare-share-hosted-link-purchase');
    if (prepareCheckout) {
      prepareCheckout.addEventListener('click', handleHostedLinkCheckout);
    }
    return (): void => {
      if (prepareCheckout) {
        prepareCheckout.removeEventListener('click', handleHostedLinkCheckout);
      }
    };
  }, [showLoadScreen]);

  const onPublishSuccess = (): void => {
    dispatch(setWebLinksData({isPublishedLink: !webLink.isPublishedLink}))
    setShowSuccessMessage(!webLink.isPublishedLink && publishItemType === PublishingAsWebpageType.PUBLISH_POSTER);
    if (copied && !webLink.isPublishedLink) {
      setCopied(false);
    }
    setIsLoading(false);
    onTogglePublish();
  };

  const onPublishError = (): void => {
    dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_publish_webpage_publish_error')));
    setIsLoading(false);
  };
  const onPublishShareLink = (): void => {
    setIsLoading(true);
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SHARE_HOSTED_LINK_PUBLISH);
    void handlePublishRequest(publishItemType, hashedId).then(onPublishSuccess).catch(onPublishError);
  };

  const onUnPublishShareLink = (): void => {
    setIsLoading(true);
    setCopied(false);
    window.PMW.gtm.trackGA4CustomEvent(PublishingAnalyticsAction.SHARE_HOSTED_LINK_UNPUBLISH);
    void handleUnpublishRequest(publishItemType, hashedId).then(onPublishSuccess).catch(onPublishError);
  };

  const getSuccessMessage = (): ReactElement | null => {
    if (!showSuccessMessage) {
      return null;
    }
    return (
      <div className={`flex-center bg-success-10 spacing-p-2 radius-8 ${inMobile ? 'spacing-m-b-6' : 'spacing-m-t-4 spacing-m-b-4'}`}>
        <Icon icon={'icon-check'} size={IconSize.SIZE_ICON_20} type={IconType.SUCCESS} className={'content-success'} />
        <Text val={window.i18next.t('pmwjs_publish_design_is_live_and_ready')} size={TextSize.XSMALL} className={'content-success spacing-m-l-1'} />
      </div>
    );
  };

  const getInputAndHeading = (): ReactElement<any> => {
    return (
      <div className={showSuccessMessage || isModalDisplay ? '' : 'spacing-p-t-5'}>
        <div className="spacing-m-b-1">
          <Text val={window.i18next.t('pmwjs_link')} className="body-xs-bold spacing-p-b-3 content-body" />
        </div>
        <InputField
          isDisabled={!webLink.isPublishedLink}
          isReadOnly
          placeholder={webLink.shareableUrl}
          input={webLink.isPublishedLink ? webLink.shareableUrl : ''}
          inputClassName={`body-xs-regular ${styles.inputFieldPadding}`}
          information={webLink.isPublishedLink ? '' : window.i18next.t('pmwjs_webpage_publish_info')}
        />
        {userPremiumLevel === USER_PREMIUM_LEVELS.PAYG && printProduct?.needsPurchase ? (
          <div className="flex-v-row spacing-p-t-6 spacing-p-b-1">
            <Text val={window.i18next.t('pmwjs_remove_watermark')} bold size={TextSize.XXSMALL} className="spacing-m-b-1 content-body" />
            <Text
              val={window.i18next.t('pmwjs_pay_amount_or_upgrade_premium', {price: printProduct?.price, redirectionLink: window.PMW.util.site_url('/premium')})}
              size={TextSize.XXSMALL}
              dangerouslySetInnerHTML
              className="content-body"
            />
          </div>
        ) : null}
      </div>
    );
  };

  const handleHostedLinkCheckout = (e: Event): void => {
    e.stopPropagation();

    if (e.target instanceof HTMLElement) {
      dispatch(setProductType(ProductType.PRINT));
      processDesignForPublishing(printProduct ?? ({} as PosterProductDataForProcessDesign));
    }
  };

  const getCopyButton = (): ReactElement<any> => {
    return (
      <CopyLinkButton
        linkToCopy={webLink.shareableUrl}
        doChangeIconOnCopy
        buttonProps={{
          type: copied ? Type.SUCCESS : Type.PRIMARY,
          text: copied ? window.i18next.t('pmwjs_insights_keywords_copied') : window.i18next.t('pmwjs_copy_link'),
          size: Size.SMALL,
          disabled: !webLink.isPublishedLink,
          onClick: () => {
            setCopied(true);
          },
          isActive: false,
        }}
        onCopyFeedbackComplete={() => {
          setCopied(false);
        }}
      />
    );
  };

  const getPublishButton = (): ReactElement<any> => {
    return !webLink.isPublishedLink ? (
      <Button
        customClasses="_cursor-pointer"
        onClick={onPublishShareLink}
        type={Type.PRIMARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_publish')}
        icon="icon-publish-arrow"
        isLoading={isLoading}
      />
    ) : (
      <Button
        customClasses="_cursor-pointer"
        onClick={onUnPublishShareLink}
        type={Type.SECONDARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.RIGHT}
        text={window.i18next.t('pmwjs_unpublish')}
        icon="icon-unlink"
        isLoading={isLoading}
      />
    );
  };

  const getPanelClasses = (): string => {
    return `${inMobile ? 'spacing-p-t-1 spacing-p-l-4 spacing-p-r-4 spacing-p-b-4' : ''} ${isModalDisplay && !inMobile ? 'spacing-p-t-7 spacing-p-b-7 spacing-p-r-12 spacing-p-l-12' : ''} flex-v-row ${!showLoadScreen ? 'animated fadeIn' : ''}`;
  };

  const getPanelContent = (): ReactElement<any> => {
    if (showLoader) {
      return <PublishingProgressBar />;
    }
    if (showLoadScreen) {
      return <PublishingLoaderScreen customHeight={isModalDisplay ? styles.customModalHeight : styles.customDropDownHeight} loaderSize={LOADER_SIZE.LARGE} />;
    }
    return (
      <>
        {getSuccessMessage()}
        {getInputAndHeading()}
        <div className={`flexbox justify-content-between ${inMobile && webLink.isPublishedLink ? 'spacing-p-t-3' : 'spacing-p-t-5'}`}>
          {getCopyButton()}
          {getPublishButton()}
        </div>
      </>
    );
  };

  return <div className={getPanelClasses()}>{getPanelContent()}</div>;
}
